import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CalendarClock, CreditCard, Sparkles, XCircle, RefreshCw } from "lucide-react";
import api, { mediaUrl, formatApiErrorDetail } from "@/lib/api";
import AppNav from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function Subscriptions() {
  const [subs, setSubs] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscriptions/me");
        setSubs(data);
      } catch (e) {
        setSubs([]);
      }
    })();
  }, []);

  const cancelSub = async (s) => {
    try {
      await api.post(`/subscriptions/${s.id}/cancel`);
      setSubs((list) => list.map((x) => (x.id === s.id ? { ...x, auto_renew: false } : x)));
      toast.success("Abbonamento annullato", {
        description: `Manterrai l'accesso fino al ${fmtDate(s.renewal_date)}.`,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };

  const reactivateSub = async (s) => {
    try {
      await api.post(`/subscriptions/${s.id}/reactivate`);
      setSubs((list) => list.map((x) => (x.id === s.id ? { ...x, auto_renew: true } : x)));
      toast.success("Rinnovo riattivato", {
        description: `L'abbonamento a ${s.creator_name} si rinnoverà il ${fmtDate(s.renewal_date)}.`,
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore");
    }
  };

  return (
    <div className="flex bg-[#0D0D0D] min-h-screen">
      <AppNav />
      <main className="flex-1 p-5 sm:p-8 pb-24 md:pb-8 overflow-x-hidden">
        <div className="max-w-3xl">
          <h1 className="text-3xl font-black font-heading mb-1">I miei abbonamenti</h1>
          <p className="text-white/50 mb-8 text-sm">Gli abbonamenti mensili attivi ai tuoi creator.</p>

          {subs === null ? (
            <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-[#E91E8C] animate-spin" /></div>
          ) : subs.length === 0 ? (
            <div className="text-center py-20 text-white/50 bg-[#1A1A1A] border border-white/10 rounded-3xl" data-testid="subs-empty">
              <Sparkles className="w-10 h-10 mx-auto mb-4 text-[#E91E8C]" />
              Nessun abbonamento attivo. Esplora i creator e abbonati per accedere ai contenuti esclusivi.
            </div>
          ) : (
            <div className="space-y-4" data-testid="subs-list">
              {subs.map((s) => {
                const canceling = s.auto_renew === false;
                return (
                  <div key={s.id} data-testid={`sub-${s.creator_id}`} className="flex items-center gap-4 bg-[#1A1A1A] border border-white/10 rounded-2xl p-5 flex-wrap">
                    <img src={mediaUrl(s.creator_avatar)} alt={s.creator_name} className="w-14 h-14 rounded-full object-cover border border-white/10" />
                    <div className="flex-1 min-w-0">
                      <Link to={`/creator/${s.creator_id}`} className="font-semibold hover:text-[#E91E8C] transition-colors">
                        {s.creator_name}
                      </Link>
                      {canceling ? (
                        <p className="text-xs text-[#F5C6D6] flex items-center gap-1.5 mt-1" data-testid={`sub-expiring-${s.creator_id}`}>
                          <XCircle className="w-3.5 h-3.5" /> In scadenza · accesso fino al {fmtDate(s.renewal_date)}
                        </p>
                      ) : (
                        <p className="text-xs text-white/50 flex items-center gap-1.5 mt-1">
                          <CalendarClock className="w-3.5 h-3.5" /> Rinnovo il {fmtDate(s.renewal_date)}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-black font-heading text-[#E91E8C]" data-testid={`sub-price-${s.creator_id}`}>€{Number(s.price).toFixed(2)}</p>
                      <p className="text-xs text-white/40">al mese</p>
                    </div>
                    {canceling ? (
                      <Button
                        onClick={() => reactivateSub(s)} size="sm" data-testid={`reactivate-sub-${s.creator_id}`}
                        className="w-full sm:w-auto rounded-full text-xs gap-1.5 bg-[#E91E8C] hover:bg-[#D8157B] text-white font-semibold"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Riattiva rinnovo
                      </Button>
                    ) : (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="outline" size="sm" data-testid={`cancel-sub-${s.creator_id}`} className="rounded-full text-xs gap-1.5 bg-transparent border-white/15 text-white/70 hover:bg-white/5 hover:text-white">
                            <XCircle className="w-3.5 h-3.5" /> Annulla abbonamento
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="bg-[#1A1A1A] border-white/10 text-white">
                          <AlertDialogHeader>
                            <AlertDialogTitle className="font-heading">Annullare l'abbonamento?</AlertDialogTitle>
                            <AlertDialogDescription className="text-white/50">
                              Sei sicuro di voler annullare l'abbonamento a {s.creator_name}? Manterrai l'accesso fino al {fmtDate(s.renewal_date)}, poi non verrà rinnovato.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-transparent border-white/10 hover:bg-white/5 text-white">Mantieni</AlertDialogCancel>
                            <AlertDialogAction onClick={() => cancelSub(s)} data-testid={`confirm-cancel-${s.creator_id}`} className="bg-[#E91E8C] hover:bg-[#D8157B] text-white">Annulla abbonamento</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-6 flex items-start gap-3 text-xs text-white/40 bg-[#1A1A1A] border border-white/10 rounded-2xl p-4">
            <CreditCard className="w-4 h-4 text-[#F5C6D6] mt-0.5 shrink-0" />
            Pagamenti simulati a scopo dimostrativo. Nessun addebito reale.
          </div>
        </div>
      </main>
    </div>
  );
}
